import {
    AIRCRAFT_COUNTABLE_ANY_FLIGHT_TIME,
    AIRCRAFT_COUNTABLE_CYCLES,
    AIRCRAFT_COUNTABLE_CYCLES_L,
    AIRCRAFT_COUNTABLE_CYCLES_R,
    AIRCRAFT_COUNTABLE_FLIGHT_TIME,
    AIRCRAFT_COUNTABLE_FUEL_GAL,
    AIRCRAFT_COUNTABLE_FUEL_KG,
    AIRCRAFT_COUNTABLE_FUEL_LB,
    AIRCRAFT_COUNTABLE_FUEL_LITERS,
    AIRCRAFT_COUNTABLE_HOBBS_TIME,
    AIRCRAFT_COUNTABLE_LANDINGS,
    AIRCRAFT_COUNTABLE_OXYGEN,
    AIRCRAFT_COUNTABLE_TACH_TIME,
    AIRCRAFT_COUNTABLE_TKS,
    type AircraftCountableType,
    type AircraftUpliftableType,
} from '@eflight/shared/dist/common/countableChanges/countables';

import { numberToPrecision } from '../../utils/NumberToPrecision';
import { decimalToHoursMinutes } from '../../utils/timeUtils';

// Countable change constants and types live in @eflight/shared (common/countableChanges/countables) —
// the flight paperwork errors need them in the mcpServer lambda too. Re-exports and UI labels stay here.
export * from '@eflight/shared/dist/common/countableChanges/countables';

export const TAG_FUEL_BEFORE_UPLIFT = 'FUEL_IS_BEFORE_UPLIFT';
export const TAG_LABELS_FOR_UPLIFT = 'TAG_LABELS_FOR_UPLIFT';
export const TAG_LABELS_FOR_CONSUMABLES = 'TAG_LABELS_FOR_CONSUMABLES';
export type CountablesOverviewTags =
    | typeof TAG_FUEL_BEFORE_UPLIFT
    | typeof TAG_LABELS_FOR_CONSUMABLES
    | typeof TAG_LABELS_FOR_UPLIFT;

export const getLabelForCountable = (
    type: AircraftCountableType | AircraftUpliftableType,
    tags: CountablesOverviewTags[] = []
): string => {
    switch (type) {
        case AIRCRAFT_COUNTABLE_ANY_FLIGHT_TIME: {
            return 'Flight hours';
        }
        case AIRCRAFT_COUNTABLE_CYCLES_L: {
            return 'Number of cycles (L)';
        }
        case AIRCRAFT_COUNTABLE_CYCLES_R: {
            return 'Number of cycles (R)';
        }
        case AIRCRAFT_COUNTABLE_FLIGHT_TIME: {
            return 'Flight time';
        }
        case AIRCRAFT_COUNTABLE_FUEL_GAL:
        case AIRCRAFT_COUNTABLE_FUEL_KG:
        case AIRCRAFT_COUNTABLE_FUEL_LB:
        case AIRCRAFT_COUNTABLE_FUEL_LITERS: {
            let fuelString;
            if (tags.includes(TAG_FUEL_BEFORE_UPLIFT)) {
                fuelString = 'Fuel before uplift';
            } else if (tags.includes(TAG_LABELS_FOR_CONSUMABLES) || tags.includes(TAG_LABELS_FOR_UPLIFT)) {
                fuelString = 'Fuel';
            } else {
                fuelString = 'Fuel on board';
            }

            return `${fuelString} (${getFuelUnit(type)})`;
        }
        case 'CYCLES': {
            return 'Number of cycles';
        }
        case 'FUEL_LITERS': {
            return 'Fuel (liters)';
        }
        case 'HOBBS_TIME': {
            return 'Hobbs time';
        }
        case 'LANDINGS': {
            return 'Landings';
        }

        case 'OIL': {
            return 'Oil';
        }
        case 'OIL_L': {
            return 'Oil Left';
        }
        case 'OIL_L_PERCENT': {
            return 'Oil Left (percent)';
        }
        case 'OIL_L_QUARTS': {
            return 'Oil Left (quarts)';
        }
        case 'OIL_PERCENT': {
            return 'Oil (percent)';
        }
        case 'OIL_QUARTS': {
            return 'Oil (quarts)';
        }
        case 'OIL_R': {
            return 'Oil Right';
        }
        case 'OIL_R_PERCENT': {
            return 'Oil Right (percent)';
        }
        case 'OIL_R_QUARTS': {
            return 'Oil Right (quarts)';
        }
        case 'OXYGEN': {
            return 'Oxygen';
        }
        case 'PRIST': {
            return 'Prist';
        }
        case 'TACH_TIME': {
            return 'Tach time';
        }
        case 'TKS': {
            return 'TKS (gallons)';
        }
    }
};

const getFuelUnit = (
    type:
        | typeof AIRCRAFT_COUNTABLE_FUEL_GAL
        | typeof AIRCRAFT_COUNTABLE_FUEL_KG
        | typeof AIRCRAFT_COUNTABLE_FUEL_LB
        | typeof AIRCRAFT_COUNTABLE_FUEL_LITERS
): string => {
    if (type === AIRCRAFT_COUNTABLE_FUEL_GAL) {
        return 'USG';
    }
    if (type === AIRCRAFT_COUNTABLE_FUEL_LB) {
        return 'Lbs';
    }
    if (type === AIRCRAFT_COUNTABLE_FUEL_KG) {
        return 'Kg';
    }
    if (type === AIRCRAFT_COUNTABLE_FUEL_LITERS) {
        return 'L';
    }
    throw new Error(`Unknown fuel unit ${type}`);
};

export const getIsCountableOil = (type: AircraftCountableType | AircraftUpliftableType): boolean =>
    type.includes('OIL');
export const getLabelForCountableShort = (type: AircraftCountableType | AircraftUpliftableType): string => {
    switch (type) {
        case AIRCRAFT_COUNTABLE_CYCLES: {
            return 'Cycles';
        }
        case AIRCRAFT_COUNTABLE_CYCLES_L: {
            return 'Cycles (L)';
        }
        case AIRCRAFT_COUNTABLE_CYCLES_R: {
            return 'Cycles (R)';
        }
        case AIRCRAFT_COUNTABLE_FUEL_GAL:
        case AIRCRAFT_COUNTABLE_FUEL_KG:
        case AIRCRAFT_COUNTABLE_FUEL_LB:
        case AIRCRAFT_COUNTABLE_FUEL_LITERS: {
            return 'Fuel';
        }
        case AIRCRAFT_COUNTABLE_HOBBS_TIME: {
            return 'Hobbs';
        }
        case AIRCRAFT_COUNTABLE_LANDINGS: {
            return 'Landings';
        }
        case AIRCRAFT_COUNTABLE_TACH_TIME: {
            return 'Tach';
        }
        case AIRCRAFT_COUNTABLE_TKS: {
            return 'TKS';
        }
        default: {
            return getLabelForCountable(type);
        }
    }
};
// noinspection JSUnusedLocalSymbols
export const getUnitForCountable = (
    type: AircraftCountableType | AircraftUpliftableType,
    tags: CountablesOverviewTags[] = []
): string | null => {
    switch (type) {
        case AIRCRAFT_COUNTABLE_FUEL_KG: {
            return 'kg';
        }
        case AIRCRAFT_COUNTABLE_OXYGEN: {
            return 'PSI';
        }
        case 'FUEL_GAL':
        case 'TKS': {
            return 'usg';
        }
        case 'FUEL_LB': {
            return 'lbs';
        }
        case 'FUEL_LITERS':
        case 'PRIST': {
            return 'l';
        }
        case 'HOBBS_TIME':
        case 'TACH_TIME': {
            return 'hours';
        }
        case 'OIL':
        case 'OIL_L':
        case 'OIL_R': {
            return 'quarts/%';
        }
        case 'OIL_L_PERCENT':
        case 'OIL_PERCENT':
        case 'OIL_R_PERCENT': {
            return '%';
        }
        case 'OIL_L_QUARTS':
        case 'OIL_QUARTS':
        case 'OIL_R_QUARTS': {
            return 'quarts';
        }
        default: {
            return null;
        }
    }
};

export const COUNTABLE_BEHAVIOR_ADDITIVE = 'additive';
export const COUNTABLE_BEHAVIOR_ABSOLUTE = 'absolute';
export const COUNTABLE_BEHAVIOR_INDETERMINATE = 'indeterminate';

export type COUNTABLE_BEHAVIOR =
    | typeof COUNTABLE_BEHAVIOR_ABSOLUTE
    | typeof COUNTABLE_BEHAVIOR_ADDITIVE
    | typeof COUNTABLE_BEHAVIOR_INDETERMINATE;

export const COUNTABLE_TYPES_ADDITIVE: AircraftCountableType[] = [
    AIRCRAFT_COUNTABLE_LANDINGS,
    AIRCRAFT_COUNTABLE_CYCLES,
    AIRCRAFT_COUNTABLE_CYCLES_L,
    AIRCRAFT_COUNTABLE_CYCLES_R,
    AIRCRAFT_COUNTABLE_FLIGHT_TIME,
];

export const COUNTABLE_TYPES_ABSOLUTE: AircraftCountableType[] = [
    AIRCRAFT_COUNTABLE_TACH_TIME,
    AIRCRAFT_COUNTABLE_HOBBS_TIME,
];
export const getCountableTypeBehavior = (type: AircraftCountableType | AircraftUpliftableType): COUNTABLE_BEHAVIOR => {
    if ((COUNTABLE_TYPES_ABSOLUTE as string[]).includes(type)) {
        return COUNTABLE_BEHAVIOR_ABSOLUTE;
    }
    if ((COUNTABLE_TYPES_ADDITIVE as string[]).includes(type)) {
        return COUNTABLE_BEHAVIOR_ADDITIVE;
    }

    return COUNTABLE_BEHAVIOR_INDETERMINATE;
};

export const getFormattedValueForCountable = (
    value: number,
    type: AircraftCountableType | AircraftUpliftableType
): string => {
    if (type === AIRCRAFT_COUNTABLE_FLIGHT_TIME) {
        return decimalToHoursMinutes(value);
    } else if (type === AIRCRAFT_COUNTABLE_TACH_TIME) {
        return value.toFixed(1);
    }

    return numberToPrecision(value, 1);
};
